import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Bounds, Center, Environment, useGLTF } from "@react-three/drei";
import { useEffect, useMemo, useRef } from "react";
import { useThree } from "@react-three/fiber";
import { EffectComposer, Bloom, SMAA } from "@react-three/postprocessing";
import * as THREE from "three";

gsap.registerPlugin(ScrollTrigger);

function RingScene({
  modelUrl = "",
  enableScroll = true,
  metalType = "",
} = {}) {
  const ringBaseRef = useRef();
  const ringDragRef = useRef();
  const cameraGroup = useRef();
  const canLoadModel = Boolean(String(modelUrl || "").trim());
  const { scene } = useGLTF(canLoadModel ? modelUrl : null);
  const { gl } = useThree();

  const hasLoggedModelRef = useRef(false);

  const yellowGoldMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: new THREE.Color("#d4af37"),
        metalness: 1,
        roughness: 0.18,
        envMapIntensity: 1.6,
      }),
    [],
  );

  const roseGoldMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: new THREE.Color("#d8a48f"),
        metalness: 1,
        roughness: 0.2,
        envMapIntensity: 1.6,
      }),
    [],
  );

  const silverMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: new THREE.Color("#d8d8d8"),
        metalness: 1,
        roughness: 0.16,
        envMapIntensity: 1.6,
      }),
    [],
  );

  const platinumMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: new THREE.Color("#c7c9cc"),
        metalness: 1,
        roughness: 0.14,
        envMapIntensity: 1.6,
      }),
    [],
  );

  const activeMetalMaterial = useMemo(() => {
    const s = String(metalType || "").toLowerCase();
    if (s.includes("platinum") || s.includes("pt")) return platinumMaterial;
    if (s.includes("rose") || s.includes("pink")) return roseGoldMaterial;
    if (s.includes("silver") || s.includes("sterling")) return silverMaterial;
    if (s.includes("white")) return silverMaterial;
    return yellowGoldMaterial;
  }, [
    metalType,
    platinumMaterial,
    roseGoldMaterial,
    silverMaterial,
    yellowGoldMaterial,
  ]);

  const diamondMaterial = useMemo(
    () =>
      new THREE.MeshPhysicalMaterial({
        color: new THREE.Color("#ffffff"),
        metalness: 0,
        roughness: 0.02,
        transmission: 1,
        ior: 2.42,
        thickness: 1,
        transparent: true,
        opacity: 1,
        envMapIntensity: 2.5,
        specularIntensity: 1,
      }),
    [],
  );

  const rubyMaterial = useMemo(
    () =>
      new THREE.MeshPhysicalMaterial({
        color: new THREE.Color("#b11226"),
        metalness: 0,
        roughness: 0.04,
        transmission: 0.6,
        ior: 1.77,
        thickness: 0.8,
        transparent: true,
        opacity: 1,
        envMapIntensity: 2.2,
        specularIntensity: 1,
      }),
    [],
  );

  const isDraggingRef = useRef(false);
  const lastPointerRef = useRef([0, 0]);
  const dragMathRef = useRef({
    qx: new THREE.Quaternion(),
    qy: new THREE.Quaternion(),
    xAxis: new THREE.Vector3(1, 0, 0),
    yAxis: new THREE.Vector3(0, 1, 0),
  });

  useEffect(() => {
    ringBaseRef.current.rotation.set(-Math.PI / 2, 0, Math.PI);

    if (!enableScroll) return;

    const triggerEl = document.querySelector("#scroll-section");
    if (!triggerEl) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: triggerEl,
        start: "top top",
        end: "bottom bottom",
        scrub: true,
      },
    });

    tl.to(ringBaseRef.current.rotation, {
      y: Math.PI * 0.75,
      x: 0.2,
    });

    tl.to(cameraGroup.current.position, {
      z: 2.2,
      y: 0.4,
    });

    tl.to(ringBaseRef.current.rotation, {
      y: Math.PI * 1.5,
      x: -0.3,
    });

    tl.to(cameraGroup.current.position, {
      z: 4,
      y: 0,
    });

    return () => tl.kill();
  }, [enableScroll]);

  useEffect(() => {
    if (!scene) return;

    if (!hasLoggedModelRef.current) {
      hasLoggedModelRef.current = true;
      scene.traverse((obj) => {
        if (obj && obj.isMesh) {
          // eslint-disable-next-line no-console
          console.log("GLB mesh:", obj.name, "material:", obj.material?.name);
        }
      });
    }

    scene.traverse((obj) => {
      if (!obj || !obj.isMesh) return;

      const meshName = (obj.name || "").toLowerCase();
      const matName = (obj.material?.name || "").toLowerCase();
      const combined = `${meshName} ${matName}`;

      // if (
      //   combined.includes("diamond") ||
      //   combined.includes("gem") ||
      //   combined.includes("stone") ||
      //   combined.includes("crystal") ||
      //   combined.includes("round") ||
      //   combined.includes("Round")
      // ) {
      //   obj.material = diamondMaterial;
      // } else {
      //   obj.material = goldMaterial;
      // }

      if (combined.includes("ruby")) {
        obj.material = rubyMaterial;
      } else if (
        combined.includes("ring") ||
        combined.includes("gold") ||
        combined.includes("metal")
      ) {
        obj.material = activeMetalMaterial;
      } else {
        obj.material = diamondMaterial;
      }

      obj.castShadow = true;
      obj.receiveShadow = true;

      if (obj.material) {
        obj.material.needsUpdate = true;
      }
    });
  }, [scene, activeMetalMaterial, diamondMaterial, rubyMaterial]);

  useEffect(() => {
    const el = gl.domElement;

    const onPointerDown = (e) => {
      isDraggingRef.current = true;
      lastPointerRef.current = [e.clientX, e.clientY];
      el.setPointerCapture?.(e.pointerId);
    };

    const onPointerUp = (e) => {
      isDraggingRef.current = false;
      el.releasePointerCapture?.(e.pointerId);
    };

    const onPointerMove = (e) => {
      if (!isDraggingRef.current || !ringDragRef.current) return;

      const [lx, ly] = lastPointerRef.current;
      const dx = e.clientX - lx;
      const dy = e.clientY - ly;
      lastPointerRef.current = [e.clientX, e.clientY];

      const { qx, qy, xAxis, yAxis } = dragMathRef.current;

      const rotSpeed = 0.01;
      qy.setFromAxisAngle(yAxis, dx * rotSpeed);
      qx.setFromAxisAngle(xAxis, dy * rotSpeed);

      const obj = ringDragRef.current;
      obj.quaternion.multiply(qy);
      obj.quaternion.multiply(qx);
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerleave", onPointerUp);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerleave", onPointerUp);
    };
  }, [gl]);

  return (
    <Bounds fit clip observe margin={1.2}>
      <Environment files="/hdri/studio_small_09_4k.exr" />
      <EffectComposer multisampling={0}>
        <SMAA />
        <Bloom intensity={0.18} luminanceThreshold={0.85} mipmapBlur />
      </EffectComposer>
      <Center>
        <group ref={cameraGroup}>
          <group ref={ringBaseRef}>
            <group ref={ringDragRef}>
              <primitive object={scene} />
            </group>
          </group>
        </group>
      </Center>
    </Bounds>
  );
}

export default RingScene;
